// AthleteDashboard.jsx — Persönliches Athleten-Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AthleteDashboard.css';

const GURTFARBEN = [
  'Weiß', 'Gelb', 'Orange', 'Grün', 'Blau', 'Rot', 'Braun', 'Schwarz', 'Dan 1', 'Dan 2', 'Dan 3',
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatDateLong(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
}
function formatPrice(v) {
  if (!v) return '0,00 €';
  return parseFloat(v).toFixed(2).replace('.', ',') + ' €';
}

function statusBadge(status) {
  const map = {
    'Angemeldet': { label: 'Angemeldet', cls: 'adash-badge--ok' },
    'Bestätigt':  { label: 'Bestätigt', cls: 'adash-badge--green' },
    'Storniert':  { label: 'Storniert', cls: 'adash-badge--red' },
    'Abgemeldet': { label: 'Abgemeldet', cls: 'adash-badge--gray' },
  };
  const s = map[status] || { label: status, cls: 'adash-badge--gray' };
  return <span className={`adash-badge ${s.cls}`}>{s.label}</span>;
}

// ── Turnier-Anmelde-Modal ────────────────────────────────────────────────────
function RegisterModal({ turnier, athleteToken, gewichtNoetig, onClose, onSuccess }) {
  const [kategorien, setKat] = useState([]);
  // Beim ERSTEN Start legt der Server den Wettkämpfer an und verlangt dafür
  // das Gewicht (routes/athleteRoutes.js, Entscheidung 16.09.2026). Ohne Feld
  // hier kam nur die Fehlermeldung — ohne Möglichkeit, es einzugeben.
  const [gewicht, setGewicht] = useState('');
  const [mitGewicht, setMitGewicht] = useState(!!gewichtNoetig);
  const [selected, setSel]  = useState('');
  const [loading, setLoad]  = useState(true);
  const [submitting, setSub] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    fetch(`/api/turniere/public/${turnier.turnier_id}/info`)
      .then(r => r.json())
      .then(d => { if (d.success) setKat(d.data.kategorien ?? []); })
      .catch(() => {})
      .finally(() => setLoad(false));
  }, [turnier.turnier_id]);

  const submit = async () => {
    setSub(true); setError(null);
    try {
      const r = await fetch(`/api/athlete/turnier/${turnier.turnier_id}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${athleteToken}`,
        },
        // Die Auswahl sind Disziplinen, keine Kategorie-Codes (siehe athleteRoutes)
        body: JSON.stringify({ disziplin: selected || null, ...(mitGewicht && { gewicht: parseFloat(gewicht) }) }),
      });
      const d = await r.json();
      if (d.success) { onSuccess(d.message); onClose(); }
      else {
        if (d.feld === 'gewicht') setMitGewicht(true);
        setError(d.error || 'Anmeldung fehlgeschlagen');
      }
    } catch { setError('Verbindungsfehler'); }
    finally { setSub(false); }
  };

  return (
    <div className="adash-modal-overlay" onClick={onClose}>
      <div className="adash-modal" onClick={e => e.stopPropagation()}>
        <div className="adash-modal-header">
          <h3>Für Turnier anmelden</h3>
          <button className="adash-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="adash-modal-body">
          <div className="adash-modal-turnier">
            <strong>{turnier.name}</strong>
            <span>{formatDateLong(turnier.datum)} · {turnier.ort}</span>
          </div>
          {loading ? (
            <div className="adash-modal-loading"><div className="adash-spinner" /></div>
          ) : (
            <div className="adash-modal-field">
              <label className="adash-label">Disziplin wählen</label>
              <select className="adash-select" value={selected} onChange={e => setSel(e.target.value)}>
                <option value="">Noch nicht sicher / Keine Präferenz</option>
                {kategorien.map((k, i) => <option key={i} value={k}>{k}</option>)}
              </select>
              <p className="adash-modal-note">
                Dein Verein trägt die genaue Kategorie (Alter, Gurt, Gewichtsklasse) für dich ein.
              </p>
            </div>
          )}
          {mitGewicht && (
            <div className="adash-modal-field">
              <label className="adash-label">Dein Gewicht (kg) *</label>
              <input className="adash-input" type="number" inputMode="decimal" step="0.1" min="15" max="250"
                value={gewicht} onChange={e => setGewicht(e.target.value)} placeholder="z.B. 65.5" />
              <p className="adash-modal-note">Nötig für die Gewichtsklasse. Gefragt wird nur bei deinem ersten Start.</p>
            </div>
          )}
          {error && <div className="adash-error">⚠ {error}</div>}
        </div>
        <div className="adash-modal-footer">
          <button className="adash-btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="adash-btn-primary" onClick={submit}
            disabled={submitting || (mitGewicht && !(parseFloat(gewicht) >= 15 && parseFloat(gewicht) <= 250))}>
            {submitting ? 'Wird angemeldet…' : 'Jetzt anmelden →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ──────────────────────────────────────────────────────────
export default function AthleteDashboard() {
  const navigate  = useNavigate();
  const token     = localStorage.getItem('athleteToken');
  const [athlete, setAthlete]     = useState(null);
  const [turniere, setTurniere]   = useState([]);
  const [anmeldungen, setAnm]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('turniere');
  const [editMode, setEditMode]   = useState(false);
  const [editForm, setEditForm]   = useState({});
  const [editSaving, setEditSave] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditOk]  = useState(false);
  const [modal, setModal]         = useState(null);   // Turnier-Objekt für Anmelde-Modal
  const [toast, setToast]         = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const authHeader = { 'Authorization': `Bearer ${token}` };

  const loadData = useCallback(async () => {
    if (!token) { navigate('/login'); return; }
    try {
      const [meR, tR, anmR] = await Promise.all([
        fetch('/api/athlete/me', { headers: authHeader }),
        fetch('/api/turniere/public'),
        fetch('/api/athlete/anmeldungen', { headers: authHeader }),
      ]);
      const [meD, tD, anmD] = await Promise.all([meR.json(), tR.json(), anmR.json()]);

      if (!meD.success) { navigate('/login'); return; }
      setAthlete(meD.data);
      setEditForm({
        vorname: meD.data.vorname, nachname: meD.data.nachname,
        geburtsdatum: meD.data.geburtsdatum?.split('T')[0] ?? '',
        geschlecht: meD.data.geschlecht ?? '',
        gurtfarbe: meD.data.gurtfarbe ?? '',
      });
      if (tD.success) setTurniere(tD.data);
      if (anmD.success) setAnm(anmD.data);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const logout = () => {
    localStorage.removeItem('athleteToken');
    localStorage.removeItem('athleteData');
    navigate('/login');
  };

  const saveProfile = async () => {
    setEditSave(true); setEditError(null); setEditOk(false);
    try {
      const r = await fetch('/api/athlete/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(editForm),
      });
      const d = await r.json();
      if (d.success) {
        setAthlete(a => ({ ...a, ...editForm }));
        setEditMode(false); setEditOk(true);
        setTimeout(() => setEditOk(false), 3000);
      } else setEditError(d.error || 'Fehler');
    } catch { setEditError('Verbindungsfehler'); }
    finally { setEditSave(false); }
  };

  // Bereits angemeldete turnier_ids
  const registeredIds = new Set(
    anmeldungen.filter(a => a.status !== 'Storniert').map(a => a.turnier_id)
  );

  if (loading) return (
    <div className="adash-page">
      <div className="adash-loading"><div className="adash-spinner" /><span>Lädt…</span></div>
    </div>
  );

  const gurtColor = {
    'Weiß':'#f0f0f0','Gelb':'#f5d200','Orange':'#f08000','Grün':'#2d8a2d',
    'Blau':'#1a5fa8','Rot':'#cc1a1a','Braun':'#7a4a1a','Schwarz':'#1a1a1a',
  };

  return (
    <div className="adash-page">
      {toast && <div className="adash-toast">{toast}</div>}
      {modal && (
        <RegisterModal
          turnier={modal}
          athleteToken={token}
          gewichtNoetig={!athlete?.wettkaempfer_id}
          onClose={() => setModal(null)}
          onSuccess={(msg) => { loadData(); showToast(msg); }}
        />
      )}

      {/* ── Header ── */}
      <header className="adash-header">
        <div className="adash-header-left">
          <div className="adash-avatar" style={{
            background: gurtColor[athlete?.gurtfarbe] ?? '#dc2626'
          }}>
            {(athlete?.vorname?.[0] ?? '?').toUpperCase()}
          </div>
          <div>
            <div className="adash-header-name">{athlete?.vorname} {athlete?.nachname}</div>
            <div className="adash-header-club">{athlete?.verein_name ?? '—'}</div>
          </div>
        </div>
        <div className="adash-header-right">
          <a className="adash-home-link" href="/">Startseite</a>
          <button className="adash-logout-btn" onClick={logout}>Abmelden</button>
        </div>
      </header>

      {/* ── Tab-Navigation ── */}
      <div className="adash-tabs">
        {[
          { id: 'turniere', label: '🏆 Turniere' },
          { id: 'anmeldungen', label: '📋 Meine Anmeldungen' },
          { id: 'profil', label: '👤 Mein Profil' },
        ].map(t => (
          <button
            key={t.id}
            className={`adash-tab${tab === t.id ? ' adash-tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="adash-content">

        {/* ━━ TURNIERE ━━ */}
        {tab === 'turniere' && (
          <div>
            <div className="adash-section-header">
              <h2>Kommende Veranstaltungen</h2>
              <p>Klicke auf „Anmelden" um dich direkt für ein Turnier zu registrieren.</p>
            </div>
            {turniere.length === 0 ? (
              <div className="adash-empty">Keine kommenden Veranstaltungen.</div>
            ) : (
              <div className="adash-turnier-list">
                {turniere.map(t => {
                  const isReg     = registeredIds.has(t.turnier_id);
                  const deadline  = t.anmeldeschluss ? new Date(t.anmeldeschluss) : null;
                  const isOpen    = !deadline || deadline > new Date();
                  return (
                    <div key={t.turnier_id} className={`adash-turnier-card${isReg ? ' adash-turnier-card--registered' : ''}`}>
                      <div className="adash-turnier-info">
                        <div className="adash-turnier-name">{t.name}</div>
                        <div className="adash-turnier-meta">
                          <span>📅 {formatDateLong(t.datum)}</span>
                          <span>📍 {t.ort}</span>
                          {deadline && <span>⏳ Anmeldung bis {formatDate(t.anmeldeschluss)}</span>}
                        </div>
                      </div>
                      <div className="adash-turnier-actions">
                        <button
                          className="adash-btn-ghost-sm"
                          onClick={() => window.open(`/event/${t.turnier_id}`, '_blank')}
                        >
                          Details
                        </button>
                        {isReg ? (
                          <span className="adash-badge adash-badge--green">✓ Angemeldet</span>
                        ) : isOpen ? (
                          <button
                            className="adash-btn-primary-sm"
                            onClick={() => setModal(t)}
                          >
                            Anmelden →
                          </button>
                        ) : (
                          <span className="adash-badge adash-badge--gray">Geschlossen</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ━━ MEINE ANMELDUNGEN ━━ */}
        {tab === 'anmeldungen' && (
          <div>
            <div className="adash-section-header">
              <h2>Meine Anmeldungen</h2>
              <p>Alle Turniere, für die du angemeldet bist oder warst.</p>
            </div>
            {anmeldungen.length === 0 ? (
              <div className="adash-empty">
                <p>Noch keine Anmeldungen vorhanden.</p>
                <button className="adash-btn-ghost" onClick={() => setTab('turniere')}>
                  Zu den Turnieren →
                </button>
              </div>
            ) : (
              <div className="adash-anm-list">
                {anmeldungen.map(a => (
                  <div key={a.id} className="adash-anm-card">
                    <div className="adash-anm-info">
                      <div className="adash-anm-name">{a.turnier_name}</div>
                      <div className="adash-anm-meta">
                        <span>📅 {formatDateLong(a.datum)}</span>
                        <span>📍 {a.ort}</span>
                        {a.division_code && <span>🥋 {a.division_code}</span>}
                        <span>Startgebühr: {formatPrice(a.startgebuehr)}</span>
                        <span style={{ color: a.bezahlt ? '#4ade80' : '#fbbf24' }}>
                          {a.bezahlt ? '✓ Bezahlt' : '⏳ Zahlung ausstehend'}
                        </span>
                      </div>
                    </div>
                    <div className="adash-anm-status">
                      {statusBadge(a.status)}
                      <div className="adash-anm-date">Angemeldet {formatDate(a.anmeldedatum)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ━━ PROFIL ━━ */}
        {tab === 'profil' && (
          <div>
            <div className="adash-section-header">
              <h2>Mein Profil</h2>
              <p>Deine persönlichen Daten. E-Mail und Verein können über den TDA-Support geändert werden.</p>
            </div>

            {editSuccess && <div className="adash-success-note">✓ Profil gespeichert</div>}

            <div className="adash-profile-card">
              {/* Nicht bearbeitbare Felder */}
              <div className="adash-profile-static">
                <div className="adash-profile-field">
                  <label>E-Mail</label>
                  <span>{athlete?.email}</span>
                </div>
                <div className="adash-profile-field">
                  <label>Verein</label>
                  <span>{athlete?.verein_name ?? '—'}{athlete?.verein_ort ? ` · ${athlete.verein_ort}` : ''}</span>
                </div>
                <div className="adash-profile-field">
                  <label>Mitglied seit</label>
                  <span>{formatDate(athlete?.created_at)}</span>
                </div>
              </div>

              <div className="adash-profile-divider" />

              {/* Bearbeitbare Felder */}
              {!editMode ? (
                <>
                  <div className="adash-profile-static">
                    <div className="adash-profile-field">
                      <label>Vorname</label><span>{athlete?.vorname}</span>
                    </div>
                    <div className="adash-profile-field">
                      <label>Nachname</label><span>{athlete?.nachname}</span>
                    </div>
                    <div className="adash-profile-field">
                      <label>Geburtsdatum</label><span>{formatDate(athlete?.geburtsdatum)}</span>
                    </div>
                    <div className="adash-profile-field">
                      <label>Geschlecht</label><span>{athlete?.geschlecht ?? '—'}</span>
                    </div>
                    <div className="adash-profile-field">
                      <label>Gürtelgrad</label><span>{athlete?.gurtfarbe ?? '—'}</span>
                    </div>
                  </div>
                  <button className="adash-btn-ghost" onClick={() => setEditMode(true)}>
                    Profil bearbeiten
                  </button>
                </>
              ) : (
                <>
                  <div className="adash-profile-form">
                    <div className="adash-form-row">
                      <div className="adash-form-field">
                        <label>Vorname *</label>
                        <input className="adash-input" type="text" value={editForm.vorname}
                          onChange={e => setEditForm(f => ({ ...f, vorname: e.target.value }))} />
                      </div>
                      <div className="adash-form-field">
                        <label>Nachname *</label>
                        <input className="adash-input" type="text" value={editForm.nachname}
                          onChange={e => setEditForm(f => ({ ...f, nachname: e.target.value }))} />
                      </div>
                    </div>
                    <div className="adash-form-row">
                      <div className="adash-form-field">
                        <label>Geburtsdatum</label>
                        <input className="adash-input" type="date" value={editForm.geburtsdatum}
                          onChange={e => setEditForm(f => ({ ...f, geburtsdatum: e.target.value }))} />
                      </div>
                      <div className="adash-form-field">
                        <label>Geschlecht</label>
                        <select className="adash-input" value={editForm.geschlecht}
                          onChange={e => setEditForm(f => ({ ...f, geschlecht: e.target.value }))}>
                          <option value="">—</option>
                          <option value="männlich">Männlich</option>
                          <option value="weiblich">Weiblich</option>
                        </select>
                      </div>
                    </div>
                    <div className="adash-form-field">
                      <label>Gürtelgrad</label>
                      <select className="adash-input" value={editForm.gurtfarbe}
                        onChange={e => setEditForm(f => ({ ...f, gurtfarbe: e.target.value }))}>
                        <option value="">—</option>
                        {GURTFARBEN.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                  </div>
                  {editError && <div className="adash-error">⚠ {editError}</div>}
                  <div className="adash-profile-actions">
                    <button className="adash-btn-ghost" onClick={() => { setEditMode(false); setEditError(null); }}>
                      Abbrechen
                    </button>
                    <button className="adash-btn-primary" onClick={saveProfile} disabled={editSaving}>
                      {editSaving ? 'Speichert…' : 'Speichern'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
